import * as React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { AppFactory } from 'app/app-factory';
import { VStack } from 'naan/primitives/stack';
import { ChapterListItem } from '../../../story/chapter-list-item';
import { CenterColumnLayout } from 'components/layouts/center-column-layout';
import { ChapterCatalogData, UnitCatalogData } from '@core/models/catalog';
import { ReturnNavState } from 'components/nav/return-nav-state';
// import { useStoryTabScrollingFx } from '../scrolling-fx';
import { SmallGap } from '@naan/primitives/spacer/gaps';
import { StoryCompleteControls } from 'components/story-detail/story-complete-controls';
import { StoryAction } from 'story/story-actions';
import { elementIds } from 'components/dom-utils/element-ids';
import { ChapterRowUiProvider } from 'story/chapter-list-item/chapter-item-contents/chapter-row-ui-model';
import { Story } from '@core/models/story-manager/story';
import {
  ChapterPositionUiProvider,
  useChapterPositionUiContext,
} from 'story/chapter-list-item/chapter-item-contents/chapter-position-ui-model';
function maybeCurrentId(chapter: ChapterCatalogData) {
  if (chapter.isCurrent) {
    return { id: elementIds.CURRENT_CHAPTER_ID };
  }

  return {};
}

const UnitHeader = ({ unit }: { unit: UnitCatalogData }) => {
  return (
    <div className="unitHeader">
      {unit.partLabel}
      <span className="unitDuration">{unit.durationDescription}</span>{' '}
    </div>
  );
};

export const StoryTab = observer(() => {
  // track when it's safe to pop back from study view
  ReturnNavState.storyDetailVisited = true;

  const { storyManager } = AppFactory.root;
  const { slug } = useParams<{ slug: string }>();
  const story = storyManager.story(slug);
  // useStoryTabScrollingFx({ unstarted: story.unstarted });

  return (
    <>
      <StoryCompleteControls />

      {/* one of these two will be non-null */}
      <StoryAction />
      <ChapterPositionUiProvider story={story}>
        <StoryTabContent story={story} />
      </ChapterPositionUiProvider>
      {/* <SoundbiteFloatingCard story={story} /> */}
    </>
  );
});

// break out content within the ui model provider so the useEffect can
// update the uiModel on initial load
const StoryTabContent = observer(({ story }: { story: Story }) => {
  const positionUiModel = useChapterPositionUiContext();
  const { progress } = story;

  React.useEffect(() => {
    positionUiModel.initWithStory(story);
  }, [
    positionUiModel,
    story,
    // these dependencies are needed to correctly reinit the state after a chapter is completed
    // (otherwise the 'whoa' state was incorrectly shown on the next chapter)
    progress.currentChapterPosition,
    // progress.firstIncompleteSoundbiteIndex,
    // progress.currentStage,
    // progress.currentStudyMode,
  ]);

  return (
    <CenterColumnLayout narrow>
      <VStack id={elementIds.CHAPTER_LIST_ID}>
        {/* <DebugChapterUiModel /> */}
        {story.units.map(unit => (
          <React.Fragment key={unit.slug}>
            {story.multiUnit ? <UnitHeader unit={unit} /> : null}
            <div>
              {unit.chapters.map(chapter => (
                <div
                  key={unit.slug + '_' + chapter.position}
                  {...maybeCurrentId(chapter)}
                >
                  <ChapterRowUiProvider chapter={chapter}>
                    <ChapterListItem chapter={chapter} />
                  </ChapterRowUiProvider>
                </div>
              ))}
            </div>
          </React.Fragment>
        ))}
      </VStack>
      <SmallGap />
    </CenterColumnLayout>
  );
});

// const DebugChapterUiModel = observer(() => {
//   const model = useStoryDetailUiContext();
//   return (
//     <div>
//       chapterPosition: {String(model.currentChapterPosition)}, soundbiteIndex:{' '}
//       {String(model.currentSoundbiteIndex)}, stage: {String(model.currentStage)}
//       , studyMode: {String(model.currentStudyMode)}
//     </div>
//   );
// });
